import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Plus, Search, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import NewProjectModal from '../components/NewProjectModal';
import StudyWorkflowMenu, { type WorkflowActionKey } from '../components/StudyWorkflowMenu';
import Topbar from '../components/Topbar';
import PatientPhoto from '../components/PatientPhoto';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../services/api';
import { invalidateProjectWorkspaceCache } from '../services/projectWorkspaceCache';
import { deletePatient, listPatients, type PatientRecord } from '../services/patientService';
import femaleIcon from '../../assets/female-icon.png';
import maleIcon from '../../assets/male-icon.png';
import { formatDateDDMMYYYY, toApiDate } from '../utils/dateFormat';
import './Dashboard.css';

type TabKey = 'recently' | 'all' | 'local';
type PatientSex = 'male' | 'female' | 'unknown';

interface Study {
  id: string;
  recordId: string;
  uploadId: string;
  patientName: string;
  dob: string;
  gender: string;
  age?: number;
  modalities: string[];
  date: string;
  sex: PatientSex;
  recently: boolean;
  studyInstanceUid: string;
  fixedSeriesInstanceUid: string;
  dicomStudies: PatientRecord['dicom_studies'];
  dicomSeries: PatientRecord['dicom_series'];
  hasPhoto: boolean;
}

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: 'recently', label: 'Recently' },
  { key: 'all', label: 'All' },
  { key: 'local', label: 'Local' },
];

const calculateAge = (birthDate: string) => {
  const parsed = new Date(toApiDate(birthDate));
  if (Number.isNaN(parsed.getTime())) return undefined;
  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  if (
    today.getMonth() < parsed.getMonth() ||
    (today.getMonth() === parsed.getMonth() && today.getDate() < parsed.getDate())
  ) {
    age -= 1;
  }
  return age >= 0 ? age : undefined;
};

const mapGender = (gender: string): PatientSex => {
  const normalized = gender.toLowerCase();
  if (normalized.startsWith('f')) return 'female';
  if (normalized.startsWith('m')) return 'male';
  return 'unknown';
};

const classificationToModality = (classification?: string | null) => {
  if (classification === 'T1w') return 'T1';
  if (classification === 'T2w' || classification === 'FLAIR') return 'T2';
  return null;
};

const PATIENT_MODALITY_ORDER = ['MR', 'T1', 'T2', 'CT'] as const;

const mapPatientRecord = (patient: PatientRecord): Study => {
  const modalitySet = new Set<string>();
  patient.dicom_series.forEach(series => {
    const modality = series.modality?.toUpperCase();
    if (modality === 'MR' || modality === 'CT') {
      modalitySet.add(modality);
    }

    if (!modality || modality === 'MR') {
      const classified = classificationToModality(series.classification);
      if (classified) modalitySet.add(classified);
    }
  });
  const modalities = PATIENT_MODALITY_ORDER.filter(modality => modalitySet.has(modality));
  const createdAt = new Date(patient.created_at);
  const recently = Date.now() - createdAt.getTime() <= 7 * 24 * 60 * 60 * 1000;

  return {
    id: `NP-${patient.id.slice(0, 8).toUpperCase()}`,
    recordId: patient.id,
    uploadId: patient.upload_id,
    patientName: patient.patient_name,
    dob: patient.birth_date,
    gender: patient.gender,
    age: calculateAge(patient.birth_date),
    modalities: modalities.length ? modalities : ['MR'],
    date: patient.created_at,
    sex: mapGender(patient.gender),
    recently,
    studyInstanceUid: patient.study_instance_uid,
    fixedSeriesInstanceUid: patient.fixed_series_instance_uid,
    dicomStudies: patient.dicom_studies,
    dicomSeries: patient.dicom_series,
    hasPhoto: Boolean(patient.has_photo),
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { accessToken, logout } = useAuth();
  const [studyList, setStudyList] = useState<Study[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [selectedWorkflowStudy, setSelectedWorkflowStudy] = useState<Study | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await listPatients(accessToken);
        if (!cancelled) setStudyList(response.patients.map(mapPatientRecord));
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          logout();
          return;
        }
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Patients could not be loaded.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [accessToken, logout]);

  const filteredStudies = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return studyList.filter(study => {
      const matchesTab = activeTab !== 'recently' || study.recently;
      const searchable = [study.id, study.patientName, study.modalities.join(' '), study.date]
        .join(' ')
        .toLowerCase();
      return matchesTab && searchable.includes(query);
    });
  }, [activeTab, searchValue, studyList]);

  const navigateToProjectWorkspace = (
    study: Study,
    workspacePreset: WorkflowActionKey | 'workspace'
  ) => {
    const query = new URLSearchParams({
      studyId: study.id,
      patientName: study.patientName,
      dob: formatDateDDMMYYYY(study.dob),
      source: 'dashboard',
      preset: workspacePreset,
    });

    navigate(
      { pathname: '/project-creation', search: `?${query.toString()}` },
      {
        state: {
          openedFrom: 'new-project',
          workspacePreset,
          uploadId: study.uploadId,
          fixedStudyInstanceUid: study.studyInstanceUid,
          fixedSeriesInstanceUid: study.fixedSeriesInstanceUid,
          dicomStudies: study.dicomStudies,
          dicomSeries: study.dicomSeries,
          study: {
            id: study.id,
            patientName: study.patientName,
            dob: study.dob,
            age: study.age,
            gender: study.gender,
            studyId: study.studyInstanceUid,
            studyInstanceUid: study.studyInstanceUid,
            modalities: study.modalities,
            date: study.date,
            source: 'local',
            patientId: study.recordId,
            hasPhoto: study.hasPhoto,
          },
        },
      }
    );
  };

  const handleDelete = async (event: React.MouseEvent<HTMLButtonElement>, study: Study) => {
    event.stopPropagation();
    if (!accessToken || !window.confirm(`Delete ${study.patientName}?`)) return;
    try {
      await deletePatient(study.recordId, accessToken);
      invalidateProjectWorkspaceCache(study.uploadId);
      setStudyList(current => current.filter(item => item.recordId !== study.recordId));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        logout();
        return;
      }
      setLoadError(error instanceof Error ? error.message : 'Patient could not be deleted.');
    }
  };

  const handleWorkflowAction = (action: WorkflowActionKey) => {
    if (!selectedWorkflowStudy) return;
    const study = selectedWorkflowStudy;
    setSelectedWorkflowStudy(null);
    navigateToProjectWorkspace(study, action);
  };

  return (
    <div className="dashboard-container">
      <main className="main-content dashboard-main-content">
        <div className="content-wrapper dashboard-content-wrapper">
          <Topbar
            title="Patient List"
            className="dashboard-shared-topbar"
          />

          <section className="table-section full-width-table-section dashboard-table-frame">
            <div className="table-header table-header-with-search dashboard-table-header">
              <div className="dashboard-table-header-left">
                <div className="dashboard-title-row">
                  <div
                    className="tabs dashboard-header-tabs"
                    role="tablist"
                    aria-label="Patient filters"
                  >
                    {tabs.map(tab => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`tab-button ${activeTab === tab.key ? 'tab-button-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                        role="tab"
                        aria-selected={activeTab === tab.key}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="dashboard-table-actions">
                <div className="search-container table-search-container">
                  <input
                    type="search"
                    className="search-input"
                    placeholder="Search patients"
                    value={searchValue}
                    onChange={event => setSearchValue(event.target.value)}
                  />
                  <Search
                    className="search-icon"
                    aria-hidden="true"
                  />
                </div>
                <button
                  type="button"
                  className="new-project-button dashboard-new-project-button"
                  onClick={() => setIsNewProjectModalOpen(true)}
                  title="New project"
                >
                  <Plus className="new-project-icon" />
                </button>
              </div>
            </div>

            {loadError && <div className="dashboard-load-error">{loadError}</div>}

            <div className="table-container">
              <table className="data-table">
                <thead className="data-table-head">
                  <tr>
                    <th className="data-table-th">ID</th>
                    <th className="data-table-th">Patient</th>
                    <th className="data-table-th">Modalities</th>
                    <th className="data-table-th">Date</th>
                    <th className="data-table-th">Status</th>
                    <th className="data-table-th data-table-th-actions">Activity</th>
                  </tr>
                </thead>
                <tbody className="data-table-body">
                  {!isLoading &&
                    filteredStudies.map((study, rowIndex) => (
                      <tr
                        key={study.recordId}
                        className={`data-table-tr ${
                          rowIndex % 2 === 0 ? 'data-table-tr-light' : 'data-table-tr-dark'
                        }`}
                        onClick={() => setSelectedWorkflowStudy(study)}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedWorkflowStudy(study);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <td className="data-table-td study-id-cell">{study.id}</td>
                        <td className="data-table-td">
                          <div className="patient-table-profile">
                            <div className="patient-table-photo">
                              <PatientPhoto
                                patientId={study.recordId}
                                hasPhoto={study.hasPhoto}
                                token={accessToken}
                                alt={`${study.patientName} patient`}
                                className="patient-table-photo-image"
                                fallback={
                                  <svg
                                    className="patient-table-photo-fallback"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={1.8}
                                    aria-label="Patient avatar"
                                    role="img"
                                  >
                                    <circle
                                      cx="12"
                                      cy="8"
                                      r="4"
                                    />
                                    <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
                                  </svg>
                                }
                              />
                            </div>
                            <div className="patient-name-container">
                              <div className="patient-name-wrapper">
                                <p className="patient-name">{study.patientName}</p>
                              </div>
                              <p className="patient-dob">
                                <span>{formatDateDDMMYYYY(study.dob)}</span>
                                {study.age !== undefined && (
                                  <span className="patient-age">({study.age}yo)</span>
                                )}
                                {study.sex !== 'unknown' && (
                                  <span
                                    className={`patient-sex-icon patient-sex-icon-${study.sex}`}
                                  >
                                    <img
                                      className="patient-sex-icon-image"
                                      src={study.sex === 'male' ? maleIcon : femaleIcon}
                                      alt={study.sex === 'male' ? 'Male' : 'Female'}
                                      draggable={false}
                                    />
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="data-table-td">
                          <div className="modalities-cell">
                            {study.modalities.map(modality => (
                              <span
                                key={`${study.recordId}-${modality}`}
                                className={`badge badge-${modality.toLowerCase()}`}
                              >
                                {modality}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="data-table-td">{formatDateDDMMYYYY(study.date)}</td>
                        <td className="data-table-td">
                          <span className="status-pill status-pill-completed">
                            <span className="status-dot status-dot-completed" />
                            Completed
                          </span>
                        </td>
                        <td className="data-table-td data-table-td-actions">
                          <div className="activity-buttons">
                            <button
                              type="button"
                              className="delete-study-button icon-only"
                              onClick={event => void handleDelete(event, study)}
                              title="Delete"
                            >
                              <Trash2 className="delete-study-icon" />
                            </button>
                            <button
                              type="button"
                              className="view-study-button icon-only"
                              onClick={event => {
                                event.stopPropagation();
                                navigateToProjectWorkspace(study, 'workspace');
                              }}
                              title="Open"
                            >
                              <ArrowRight className="view-study-icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  {isLoading && (
                    <tr>
                      <td
                        className="empty-table-cell"
                        colSpan={6}
                      >
                        Loading patients...
                      </td>
                    </tr>
                  )}
                  {!isLoading && filteredStudies.length === 0 && (
                    <tr>
                      <td
                        className="empty-table-cell"
                        colSpan={6}
                      >
                        No patients found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>

      {isNewProjectModalOpen && <NewProjectModal onClose={() => setIsNewProjectModalOpen(false)} />}
      {selectedWorkflowStudy && (
        <StudyWorkflowMenu
          study={selectedWorkflowStudy}
          onClose={() => setSelectedWorkflowStudy(null)}
          onSelectAction={handleWorkflowAction}
        />
      )}
    </div>
  );
};

export default Dashboard;
